import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../api/Users';
import { User, UserRegistrationData } from '../types';
import { getAllProfessions, Profession } from '../api/Professions';

const AdminUserAdd: React.FC = () => {
  const [user, setUser] = useState<Partial<UserRegistrationData & User>>({ role: 'user', suspended: false, professionID: '' });
  const [professions, setProfessions] = useState<Profession[]>([]);
  useEffect(() => {
    getAllProfessions().then(setProfessions).catch(() => setProfessions([]));
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // Validate profession selection
    if (!user.professionID) {
      setError('Please select a profession.');
      return;
    }
    setLoading(true);
    try {
      const userData = {
        email: user.email || '',
        password,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone,
        role: user.role,
        suspended: user.suspended,
        professionID: user.professionID,
      };
      await createUser(userData);
      setSuccess('User created successfully.');
      setTimeout(() => navigate('/admin'), 1000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2>Add User</h2>
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input className="form-control" name="email" value={user.email || ''} onChange={handleChange} required type="email" />
        </div>
        <div className="mb-3">
          <label className="form-label">First Name</label>
          <input className="form-control" name="firstName" value={user.firstName || ''} onChange={handleChange} />
        </div>
        <div className="mb-3">
          <label className="form-label">Last Name</label>
          <input className="form-control" name="lastName" value={user.lastName || ''} onChange={handleChange} />
        </div>
        <div className="mb-3">
          <label className="form-label">Phone</label>
          <input className="form-control" name="phone" value={user.phone || ''} onChange={handleChange} />
        </div>
        <div className="mb-3">
          <label className="form-label">Password</label>
          <input className="form-control" name="password" value={password} onChange={e => setPassword(e.target.value)} required type="password" autoComplete="new-password" minLength={6} />
        </div>
        <div className="mb-3">
          <label className="form-label">Confirm Password</label>
          <input className="form-control" name="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required type="password" autoComplete="new-password" minLength={6} />
        </div>
        <div className="mb-3">
          <label className="form-label">Profession</label>
          <select className="form-select" name="professionID" value={user.professionID || ''} onChange={handleChange} required>
            <option value="">Select a profession</option>
            {professions.map((p) => (
              <option key={p.professionID} value={p.professionID}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Role</label>
          <select className="form-select" name="role" value={user.role} onChange={handleChange}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <button className="btn btn-primary me-2" type="submit" disabled={loading}>Add User</button>
        <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>Cancel</button>
      </form>
    </div>
  );
};

export default AdminUserAdd;
